/**
 * @module features/devices/components/DecommissionDialog
 * @description Confirmation modal for retiring a device (soft-delete). Makes it
 * explicit that the MAC and registered ID stay locked and the unit is restorable.
 */
import * as React from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AppError } from "@/lib/api-client";
import { useDecommissionDevice } from "../api/useDeviceMutations";
import { DeviceItem } from "../types";

interface DecommissionDialogProps {
  device: DeviceItem | null;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export function DecommissionDialog({ device, open, onClose, onDone }: DecommissionDialogProps) {
  const mutation = useDecommissionDevice();
  const [error, setError] = React.useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const confirm = async () => {
    if (!device) return;
    setError(null);
    try {
      await mutation.mutateAsync(device.id);
      onDone?.();
      handleClose();
    } catch (err) {
      setError(
        err instanceof AppError
          ? err.errorDetails
          : "Failed to decommission the device. Please try again."
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[420px]" sheetOnMobile>
        <DialogHeader>
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10 text-destructive mb-2">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </div>
          <DialogTitle className="text-center text-base">Decommission device</DialogTitle>
          <DialogDescription className="text-center text-xs">
            <strong className="text-foreground">{device?.device_name}</strong> will be retired and
            hidden from the active fleet. Its MAC address and registered ID stay locked, and you
            can reactivate it later.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div role="alert" className="p-3 rounded-lg bg-danger-soft text-danger text-xs border border-danger/20">
            {error}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between pt-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={confirm}
            disabled={mutation.isPending}
            className="min-w-[120px]"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Working...
              </>
            ) : (
              "Decommission"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
